export const TEMP_ID_PREFIX = 'temp_'

export const isTempId = (id: string): boolean => id.startsWith(TEMP_ID_PREFIX)

export const generateTempId = (): string => `${TEMP_ID_PREFIX}${crypto.randomUUID()}`

interface DeferredId {
  promise: Promise<string>
  resolve: (realId: string) => void
  reject: (error: Error) => void
}

export class TempIdResolver {
  private pending = new Map<string, DeferredId>()
  private resolved = new Map<string, string>()

  register(tempId: string): Promise<string> {
    let resolve!: (realId: string) => void
    let reject!: (error: Error) => void
    const promise = new Promise<string>((res, rej) => {
      resolve = res
      reject = rej
    })
    // Suppress unhandled rejection on the stored promise — callers use waitForReal instead
    promise.catch(() => {})
    this.pending.set(tempId, { promise, resolve, reject })
    return promise
  }

  resolve(tempId: string, realId: string): void {
    const deferred = this.pending.get(tempId)
    if (!deferred) return
    this.resolved.set(tempId, realId)
    this.pending.delete(tempId)
    deferred.resolve(realId)
  }

  reject(tempId: string, error: Error): void {
    const deferred = this.pending.get(tempId)
    if (!deferred) return
    this.pending.delete(tempId)
    deferred.reject(error)
  }

  async waitForReal(id: string): Promise<string> {
    if (!isTempId(id)) return id
    const alreadyResolved = this.resolved.get(id)
    if (alreadyResolved) return alreadyResolved
    const deferred = this.pending.get(id)
    if (deferred) return deferred.promise
    return id
  }

  clear(): void {
    for (const [, deferred] of this.pending) {
      deferred.reject(new Error('TempIdResolver cleared'))
    }
    this.pending.clear()
    this.resolved.clear()
  }
}
